/**
 * MandatoryRegistrationGate
 *
 * A hard, full-screen gate that blocks all dashboard rendering until the
 * user has completed the BIG5 / OCEAN personality assessment.
 *
 * Why this exists (per Feb-26 P0 directive from the product owner):
 *   "The BIG5/OCEAN assessment and all registration questions are not
 *    optional — they are the foundation of everything HiBFF does. The
 *    answers directly determine which challenges AvA assigns to the teen
 *    and how the meme / NFT generation pipeline personalises every card."
 *
 * Gate logic:
 *   • Checks `users.ocean_completed_at` via /api/ocean/status.
 *   • If completed → renders {children}, gate is invisible.
 *   • If NOT completed → renders <OceanAssessment /> WITHOUT the postpone
 *     button (postpone is removed by a CSS rule injected into the gate
 *     scope), and refuses to render children until completion.
 *   • Loading state shows a minimal spinner — never the dashboard.
 *
 * Returning-user safety:
 *   Once ocean is complete, status flips permanently, and the gate is a
 *   silent passthrough. Persisted server-side, so it works across devices.
 *
 * Parents are exempt by default (the assessment is the teen's personality
 * profile that drives challenge assignment). Parents see the gate only if
 * they registered a teen profile under their own account.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import OceanAssessment from './OceanAssessment';
import { apiClient } from '../../api/client';

const MandatoryRegistrationGate = ({ user, children }) => {
  const [phase, setPhase] = useState('loading'); // loading | gate | open
  const [error, setError] = useState(null);

  const role = (user?.role || '').toLowerCase();
  // Accounts whose registration is already complete (onboarding_complete is
  // set by Maya completion itself, or the account was provisioned via a
  // gifted plan) must never be re-gated. Genuinely new sign-ups carry
  // onboarding_complete=false until they finish the 22 questions.
  const provisioned = user?.onboarding_complete === true || user?.is_gifted_subscription === true;
  const exempt = provisioned || role === 'parent' || role === 'admin' || role === 'school_admin' || role === 'corporate_admin' || role === 'corporate_user';

  useEffect(() => {
    let cancelled = false;
    if (exempt || !user) {
      setPhase('open');
      return;
    }
    (async () => {
      try {
        const data = await apiClient.request('/api/ocean/status');
        if (cancelled) return;
        if (data?.has_completed) {
          setPhase('open');
        } else {
          setPhase('gate');
        }
      } catch (e) {
        if (cancelled) return;
        // If we can't reach the API, fail OPEN — better to let the user in
        // than lock them out of the platform entirely.
        setError(e.message);
        setPhase('open');
      }
    })();
    return () => { cancelled = true; };
  }, [user, exempt]);

  if (phase === 'loading') {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF8F0 0%, #FFEDE0 25%, #F3E8FF 75%, #E9D5FF 100%)' }} data-testid="reg-gate-loading">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7B4FD8' }} />
      </div>
    );
  }

  if (phase === 'gate') {
    return (
      <div data-testid="reg-gate-active">
        {/* Hide OceanAssessment's "Remind me tomorrow" button — gate is mandatory */}
        <style>{`[data-testid="ocean-postpone-btn"] { display: none !important; }`}</style>
        <OceanAssessment
          onComplete={() => setPhase('open')}
          onPostpone={() => {/* postpone intentionally disabled inside the gate */}}
        />
      </div>
    );
  }

  // Gate open → render dashboard normally
  return <>{children}</>;
};

export default MandatoryRegistrationGate;
